import { NativeModules } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import { getTenantId, getWhitelabelConfig } from '../../base/whitelabel/functions';
import { getPersistentJson } from '../../vrs-auth/storage';
import { redactDiagnosticRecord } from './diagnosticRedaction';
import { mobileLog } from './logging';
import { flushTelemetryQueue, queueTelemetryEvent } from './telemetryQueue';
// Memory-only source for this page/app runtime. Stored sessions can be shared
// by browser tabs; their measurements must never be folded into one timeline.
const telemetryObserverId = uuidv4();
/** This runtime's observer id, for telemetry sent outside emitMobileTelemetryEvent. */
export function getTelemetryObserverId() {
    return telemetryObserverId;
}
function getSessionId() {
    return getPersistentJson('vrs_session')?.sessionId;
}
function getTenantContext() {
    const config = getWhitelabelConfig();
    return {
        appType: config?.operations?.appType,
        tenantId: getTenantId()
    };
}
function getReleaseContext() {
    const appInfo = (NativeModules?.AppInfo || {});
    return {
        buildNumber: typeof appInfo.buildNumber === 'string' || typeof appInfo.buildNumber === 'number'
            ? String(appInfo.buildNumber)
            : undefined,
        version: typeof appInfo.version === 'string' ? appInfo.version : undefined
    };
}
// Kept below the server's platform limit so a long user agent can never cost
// us the event it was attached to.
const PLATFORM_LABEL_MAX_LENGTH = 80;
function getPlatformLabel() {
    const nav = typeof navigator !== 'undefined'
        ? navigator
        : undefined;
    if (nav?.product === 'ReactNative') {
        return 'native';
    }
    // A browser user agent runs well past the field's length limit (Chrome is
    // ~117 characters against a cap of 80), and an over-length field rejects
    // the WHOLE event. That is why every web room-boot breadcrumb was dropped
    // while native — which has no userAgent and sent 'native' — came through.
    return (nav?.userAgent || 'unknown').slice(0, PLATFORM_LABEL_MAX_LENGTH);
}
function getSurfaceLabel() {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    return nav?.product === 'ReactNative' ? 'mobile' : 'web';
}
export function emitMobileTelemetryEvent(event, metadata = {}, options = {}) {
    mobileLog('info', event, metadata, { console: false });
    const tenant = getTenantContext();
    const release = getReleaseContext();
    queueTelemetryEvent(redactDiagnosticRecord({
        appType: tenant.appType,
        buildNumber: release.buildNumber,
        callId: options.callId,
        event,
        metadata: { ...metadata, observerId: telemetryObserverId },
        platform: getPlatformLabel(),
        requestId: options.requestId,
        roomName: options.roomName,
        sessionId: getSessionId(),
        state: options.state,
        surface: getSurfaceLabel(),
        tenantId: tenant.tenantId,
        version: release.version
    }));
    if (options.keepalive) {
        // Sent while the page unloads: flush the batch now, with keepalive,
        // so it outlives the page (the queue's own pagehide flush may
        // already have run).
        void flushTelemetryQueue({ keepalive: true });
    }
}
